import { Box } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { trackingService, type TrackingStatsRow } from '../services/tracking.service';
import { activityReportWindow, activityReportFilter } from './activity-report';
import { Framed, Row, Num, SectionTitle } from './activity-table';

// The same report the graph uses, so the numbers agree by construction. `os` is already
// collapsed to the family there, which stops the table filling with flavours of one iOS.
const DIMENSIONS: Array<{ key: string; title: string }> = [
  { key: 'country', title: 'Country' },
  { key: 'device', title: 'Device' },
  { key: 'os', title: 'OS' },
];

const ROWS = 5;

function Table({ title, rows, total }: { title: string; rows: TrackingStatsRow[]; total: number }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <SectionTitle hint="of visits">{title}</SectionTitle>
      <Framed cols="1fr 56px">
        {rows.length === 0 && <Row label="—" />}
        {rows.slice(0, ROWS).map((r) => {
          const visits = Number(r.visits) || 0;
          // Of the entity's whole traffic, not of the five rows shown — that would inflate
          // every share once there is a tail.
          const pct = total > 0 ? Math.round((visits / total) * 1000) / 10 : 0;
          const name = String(r.name || r.group_key || '(unknown)');
          return <Row key={name} label={name} title={name}><Num>{pct}%</Num></Row>;
        })}
      </Framed>
    </Box>
  );
}

export default function ActivityDistributions({ kind, entityId, open }: {
  kind: string;
  entityId: string;
  open: boolean;
}) {
  const results = useQueries({
    queries: DIMENSIONS.map(({ key }) => ({
      queryKey: ['activity-distribution', kind, entityId, key],
      queryFn: () => trackingService.getStats({
        ...activityReportWindow(),
        ...activityReportFilter(kind, entityId),
        group_by: key,
        sort_by: 'visits',
        sort_order: 'desc',
        limit: ROWS,
      }),
      enabled: open && !!kind && !!entityId,
      staleTime: 60_000,
    })),
  });

  if (results.some((r) => r.isLoading)) return null;
  if (!results.some((r) => (r.data?.rows?.length ?? 0) > 0)) return null;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 0.5 }}>
      {DIMENSIONS.map(({ key, title }, i) => (
        <Table
          key={key}
          title={title}
          rows={results[i].data?.rows ?? []}
          total={Number(results[i].data?.totals?.visits) || 0}
        />
      ))}
    </Box>
  );
}
